import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ScanProfileDto } from '@terra/shared-contracts-external-network';
import { externalNetworkScanProfilesApi } from '../../clients/tester-client/external-network-scan-profiles-api';
import { EXTERNAL_NETWORK_DETAIL_KEY } from './useSurfaceSettings';
import { TARGETS_QUERY_KEY } from '../useTargets';

export const EXTERNAL_NETWORK_SCAN_PROFILES_KEY =
  'external-network-scan-profiles';

const EMPTY_SCAN_PROFILES: ScanProfileDto[] = [];

export function useExternalNetworkScanProfiles({
  enabled = true,
}: { enabled?: boolean } = {}) {
  const result = useQuery({
    queryKey: [EXTERNAL_NETWORK_SCAN_PROFILES_KEY],
    queryFn: () => externalNetworkScanProfilesApi.listScanProfiles(),
    enabled,
  });

  return {
    profiles: result.data ?? EMPTY_SCAN_PROFILES,
    isLoading: result.isLoading,
    isError: result.isError,
  };
}

export function useAssignExternalNetworkScanProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      surfaceId,
      scanProfileId,
    }: {
      surfaceId: string;
      scanProfileId: string;
    }) =>
      externalNetworkScanProfilesApi.assignExternalNetworkScanProfile({
        surfaceId,
        scanProfileId,
      }),
    onSuccess: async (_profile, { surfaceId }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [EXTERNAL_NETWORK_SCAN_PROFILES_KEY],
        }),
        queryClient.invalidateQueries({
          queryKey: [EXTERNAL_NETWORK_DETAIL_KEY, surfaceId],
        }),
        queryClient.invalidateQueries({ queryKey: [TARGETS_QUERY_KEY] }),
      ]);
    },
  });
}
